import { fetchEventSource } from '@microsoft/fetch-event-source';
import { EVENT_SOURCE_EVENTS, HTTP_STATUS, READY_STATE } from '~/composables/mercure/shared/constants';
import type { EventListener } from '~/composables/mercure/shared/types';

export interface EventSourceConfig {
  url: string;
  headers?: Record<string, string>;
  withCredentials?: boolean;
}

class StreamEndedByServerError extends Error {}

export class ConnectionRefusedError extends Error {
  constructor (readonly status: number) {
    super(`Connection failed with status: ${status}`);
  }
}

export class EventSourceAdapter {
  private abortController: AbortController | null = null;
  private readonly eventListeners: Map<string, Set<EventListener>>;
  private isConnecting: boolean = false;
  private _isConnected: boolean = false;

  constructor () {
    this.eventListeners = new Map();
  }

  connect (config: EventSourceConfig): void {
    if (this.abortController) {
      throw new Error('EventSource already connected');
    }

    if (this.isConnecting) {
      throw new Error('EventSource connection already in progress');
    }

    this.isConnecting = true;
    this.abortController = new AbortController();

    fetchEventSource(config.url, {
      headers: config.headers || {},
      signal: this.abortController.signal,
      credentials: config.withCredentials ? 'include' : 'same-origin',
      openWhenHidden: true,

      onopen: (response): void => {
        if (response.ok && response.headers.get('content-type')?.includes('text/event-stream')) {
          this._isConnected = true;
          this.isConnecting = false;
          this.emit(EVENT_SOURCE_EVENTS.OPEN, {});
          return;
        } else if (response.status >= HTTP_STATUS.CLIENT_ERROR_MIN &&
                   response.status < HTTP_STATUS.CLIENT_ERROR_MAX &&
                   response.status !== HTTP_STATUS.RATE_LIMIT) {
          throw new ConnectionRefusedError(response.status);
        } else {
          throw new Error(`Unexpected response: ${response.status}`);
        }
      },

      onmessage: (event) => {
        if (!this._isConnected) {
          return;
        }
        this.emit(EVENT_SOURCE_EVENTS.MESSAGE, event);
      },

      onerror: (error) => {
        if (this.abortController?.signal.aborted) {
          throw new Error('Connection aborted');
        }

        if (error instanceof StreamEndedByServerError) {
          return;
        }

        this._isConnected = false;

        const err = error instanceof Error ? error : new Error(String(error));
        this.emit(EVENT_SOURCE_EVENTS.ERROR, err);

        if (error instanceof ConnectionRefusedError) {
          this.isConnecting = false;
          this.abortController = null;
          throw error;
        }
      },

      onclose: () => {
        if (this.abortController?.signal.aborted) {
          return;
        }

        this._isConnected = false;
        this.isConnecting = false;
        this.emit(EVENT_SOURCE_EVENTS.CLOSE, {});

        // fetchEventSource reopens a stream the server ended only when onclose throws.
        throw new StreamEndedByServerError();
      },
    }).catch((error) => {
      if (error instanceof ConnectionRefusedError) {
        return;
      }

      if (!this.abortController?.signal.aborted) {
        this.isConnecting = false;
        this._isConnected = false;
        this.abortController = null;
        const err = error instanceof Error ? error : new Error(String(error));
        this.emit(EVENT_SOURCE_EVENTS.ERROR, err);
      }
    });
  }

  close (): void {
    if (!this.abortController) {
      return;
    }

    this.abortController.abort();
    this.abortController = null;
    this._isConnected = false;

    this.emit(EVENT_SOURCE_EVENTS.CLOSE, {});
  }

  isConnected (): boolean {
    return this._isConnected;
  }

  getReadyState (): number {
    return this._isConnected ? READY_STATE.OPEN : READY_STATE.CLOSED;
  }

  on<T = any>(event: string, listener: EventListener<T>): () => void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }

    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.add(listener);
    }

    return () => {
      this.eventListeners.get(event)?.delete(listener);
    };
  }

  private emit<T = any>(event: string, data: T): void {
    const listeners = this.eventListeners.get(event);
    if (!listeners || listeners.size === 0) {
      return;
    }

    for (const listener of listeners) {
      try {
        listener(data);
      } catch (error) {
        console.error(`[EventSourceAdapter] Listener error for "${event}":`, error);
      }
    }
  }
}
